import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import { analyticsService } from '../services/AnalyticsService'
import { useAnalytics } from '../hooks/useAnalytics'

/**
 * AnalyticsContext - Provides analytics functionality throughout the app
 * 
 * Wraps the useAnalytics hook in a context for global access and
 * handles automatic initialization and cleanup.
 */

interface AnalyticsContextType {
  // Session info
  currentSession: ReturnType<typeof useAnalytics>['currentSession']
  queueSize: number
  isInitialized: boolean
  
  // Tool tracking methods
  trackToolStart: ReturnType<typeof useAnalytics>['trackToolStart']
  trackToolComplete: ReturnType<typeof useAnalytics>['trackToolComplete']
  trackToolLoaded: ReturnType<typeof useAnalytics>['trackToolLoaded']
  
  // File operations
  trackFileUpload: ReturnType<typeof useAnalytics>['trackFileUpload']
  trackFileDownload: ReturnType<typeof useAnalytics>['trackFileDownload']
  
  // Analysis tracking
  trackAnalysisStart: ReturnType<typeof useAnalytics>['trackAnalysisStart']
  trackAnalysisComplete: ReturnType<typeof useAnalytics>['trackAnalysisComplete']
  
  // Navigation tracking
  trackNavigation: ReturnType<typeof useAnalytics>['trackNavigation']
  
  // Error tracking
  trackError: ReturnType<typeof useAnalytics>['trackError']
  
  // Specialized tracking
  trackStepProgression: ReturnType<typeof useAnalytics>['trackStepProgression']
  trackPageLoaded: ReturnType<typeof useAnalytics>['trackPageLoaded']
  trackFeatureUsage: ReturnType<typeof useAnalytics>['trackFeatureUsage']
  trackSessionEvent: ReturnType<typeof useAnalytics>['trackSessionEvent']
  
  // Custom events
  trackCustomEvent: ReturnType<typeof useAnalytics>['trackCustomEvent']
  
  // Utility methods
  flush: ReturnType<typeof useAnalytics>['flush']
  endSession: ReturnType<typeof useAnalytics>['endSession']
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined)

interface AnalyticsProviderProps {
  children: ReactNode
}

/**
 * AnalyticsProvider component
 * 
 * Wraps the application to provide analytics functionality.
 * Should be placed high in the component tree.
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps): JSX.Element {
  const analytics = useAnalytics()

  // Handle app lifecycle events
  useEffect(() => {
    // Analytics is automatically initialized by useAnalytics hook
    
    // Clean up on unmount
    return () => {
      analyticsService.endSession()
    }
  }, [])

  // Handle browser events (session end only)
  useEffect(() => {
    const handleBeforeUnload = () => {
      analyticsService.endSession()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * Hook to access analytics context
 * 
 * Must be used within an AnalyticsProvider
 */
export function useAnalyticsContext(): AnalyticsContextType {
  const context = useContext(AnalyticsContext)
  
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider')
  }
  
  return context
}

export { AnalyticsContext }